/**
 * C3.3 — the closed profiling vocabulary, shared across the wire.
 *
 * Profiling is not a system. A screener, a category-usage question and an age
 * band are ordinary canonical research questions that happen to earn a role,
 * and the role rides on the existing `tags` array — reaching compiled SurveyJS
 * as `x_dt_tags` and analysis as a named cut. There is no parallel
 * `segmentation` block and no second question list.
 *
 * TypeScript mirror of `apps/python-api/models/questionnaire/profiling.py`,
 * constant for constant. Python stamps these tags; this side reads them off
 * persisted drafts and compiled surveys.
 * `tests/apps/python-api/test_questionnaire_profiling_vocabulary.py` is the
 * drift guard.
 *
 * Cache scope: not applicable. No LLM prompt is assembled here.
 */

import type { PlanProfilingRole } from "./plan";

/**
 * Namespaced like every other code-owned tag family (`plan:`, `lp:`). An
 * unprefixed `demographic` tag would collide with anything else wanting the
 * word.
 */
export const PROFILE_TAG_PREFIX = "profile:";

export const PROFILING_ROLES = [
  "eligibility",
  "behavioral",
  "demographic",
  "attitudinal",
] as const satisfies ReadonlyArray<PlanProfilingRole>;

export const PROFILE_TAGS = [
  "profile:eligibility",
  "profile:behavioral",
  "profile:demographic",
  "profile:attitudinal",
] as const;

/**
 * Which roles may become a named analysis cut.
 *
 * `eligibility` is deliberately absent. A screener is asked so that respondents
 * who fail it are not in the study; among the respondents who ARE in the study
 * it is a constant, so cutting on it yields one group containing everybody and
 * a report that presents that as a finding.
 */
export const CUT_ELIGIBLE_PROFILING_ROLES = [
  "behavioral",
  "demographic",
  "attitudinal",
] as const satisfies ReadonlyArray<PlanProfilingRole>;

/** The tag for one role. The only place a `profile:` string is constructed. */
export function profileTag(role: PlanProfilingRole): string {
  if (!PROFILING_ROLES.includes(role)) {
    throw new Error(
      `unknown profiling role '${role}'; expected one of ${PROFILING_ROLES.join(", ")}`,
    );
  }
  return `${PROFILE_TAG_PREFIX}${role}`;
}

/**
 * The role a tag names, or `null` if it names no profiling role.
 *
 * `null` covers both "not a profiling tag" and "malformed profiling tag"
 * (`profile:psychographic`, `profile:`) on purpose: in both cases stamping did
 * not say the question was profiled, so a reader must not treat it as if it
 * had been.
 */
export function parseProfileTag(tag: string): PlanProfilingRole | null {
  if (!tag.startsWith(PROFILE_TAG_PREFIX)) {
    return null;
  }
  const role = tag.slice(PROFILE_TAG_PREFIX.length);
  return PROFILING_ROLES.includes(role as PlanProfilingRole)
    ? (role as PlanProfilingRole)
    : null;
}

/**
 * The single profiling role a tag list carries, if exactly one.
 *
 * Ambiguity resolves to `null` rather than to a first match: a question tagged
 * both `profile:demographic` and `profile:behavioral` was stamped by something
 * that is not the assembly tag builder, which emits at most one.
 */
export function profilingRoleOf(
  tags: ReadonlyArray<string> | null | undefined,
): PlanProfilingRole | null {
  if (!Array.isArray(tags)) {
    return null;
  }
  const roles = new Set<PlanProfilingRole>();
  for (const tag of tags) {
    if (typeof tag !== "string") {
      continue;
    }
    const role = parseProfileTag(tag);
    if (role !== null) {
      roles.add(role);
    }
  }
  return roles.size === 1 ? [...roles][0] : null;
}

/** Whether a role may be offered as a named analysis cut. */
export function isCutEligible(role: PlanProfilingRole | null): boolean {
  return (
    role !== null &&
    (CUT_ELIGIBLE_PROFILING_ROLES as ReadonlyArray<string>).includes(role)
  );
}

/**
 * The tag PLAN's declared segmentation cut stamps (`analysisUse`
 * `segmentation_cut`): analysis splits the study's primary read by each such
 * cut it offers. Mirror of `PLAN_SEGMENTATION_CUT_TAG`
 * (`models/questionnaire/contracts.py`).
 */
export const PLAN_SEGMENTATION_CUT_TAG = "plan:use:segmentation_cut";
/** The prefix of a question's one use tag (PLAN's `analysisUse`). Mirror of `PLAN_USE_TAG_PREFIX`. */
export const PLAN_USE_TAG_PREFIX = "plan:use:";
